type ElementCollection =
  | HTMLElement
  | Element
  | NodeListOf<Element>
  | HTMLCollectionOf<HTMLElement>
  | HTMLCollectionOf<Element>;

interface Module {
  default: any;
}

/**
 * Import and instantiate a class when element is scrolled into the frame.
 *
 * @param {Function} importFunction - the dynamic import function used to import the class file. This should look like import(\/* webpackChunkName: "className" *\/ 'path/to/class/file').
 * @param {ElementCollection} observedElements - a DOM element or list of DOM elements for the IntersectionObserver to observe.
 * @param {boolean} shouldInstantiate - whether or not the imported module should be newed up.
 * @param {IntersectionObserverInit} observerOptions - options to pass to the IntersectionObserver constructor
 * @param {...any} otherConstructorArgs - any other arguments to pass to the constructor.
 */
export function importClassOnScroll(
  importFunction: () => Promise<Module>,
  observedElements: ElementCollection,
  shouldInstantiate: boolean,
  observerOptions?: IntersectionObserverInit,
  ...otherConstructorArgs: any[]
) {
  const elementList = convertToArray(observedElements);

  const observer = new IntersectionObserver((entries) => {
    entries?.forEach(async (entry) => {
      if (entry?.isIntersecting) {
        try {
          const importedClass = await importFunction();

          if (shouldInstantiate) {
            const instantiated = new importedClass.default(
              entry.target,
              ...otherConstructorArgs
            );
          }

          observer.unobserve(entry.target);
        } catch (error) {
          console.error(
            "An error occurred when trying to dynamically import a class."
          );
          console.error(error);
          return;
        }
      }
    });
  }, observerOptions);

  elementList.forEach((element) => {
    observer.observe(element);
  });
}

/**
 * Import and instantiate a class when element is clicked, focused, etc.
 *
 * @param {Function} importFunction - the dynamic import function used to import the class file. This should look like import(\/* webpackChunkName: "className" *\/ 'path/to/class/file').
 * @param {ElementCollection} clickableElements - a DOM element or list of DOM elements that would be clicked.
 * @param {Event} eventType - the type of event to listen for
 * @param {boolean} shouldInstantiate - whether or not the imported module should be newed up.
 * @param {boolean | AddEventListenerOptions} listenerOptions - options to pass to the IntersectionObserver constructor
 * @param {...any} otherConstructorArgs - any other arguments to pass to the constructor.
 */
export function importClassOnEvent(
  importFunction: () => Promise<Module>,
  elementsToListenOn: ElementCollection,
  eventType: string | string[],
  shouldInstantiate: boolean,
  listenerOptions?: boolean | AddEventListenerOptions,
  ...otherConstructorArgs: any[]
) {
  const elementList = convertToArray(elementsToListenOn);
  const events = typeof eventType === "string" ? [eventType] : eventType;

  elementList?.forEach((element) => {
    events.forEach((event) => {
      element.addEventListener(
        event,
        async function listener(event) {
          const importedClass = await importFunction();

          if (shouldInstantiate) {
            const instantiated = new importedClass.default(
              event.currentTarget,
              ...otherConstructorArgs
            );
          }

          this.removeEventListener(eventType, listener);
        },
        listenerOptions
      );
    });
  });
}

/**
 * Import and instantiate a class unconditionally.
 *
 * @param {Function} importFunction - the dynamic import function used to import the class file. This should look like import(\/* webpackChunkName: "className" *\/ 'path/to/class/file').
 * @param {boolean} shouldInstantiate - whether or not the imported module should be newed up.
 * @param {...any} constructorArgs - arguments to pass to the constructor.
 */

export async function importClass(
  importFunction: () => Promise<Module>,
  shouldInitialize: boolean,
  constructorArgs: any[]
) {
  await importFunction().then((module) => {
    if (shouldInitialize) {
      const instantiated = new module.default(...constructorArgs);
    }
  });
}

export function convertToArray(
  elements: ElementCollection
): Array<HTMLElement | Element> {
  if (!elements) {
    return [];
  }

  if ("length" in elements) {
    return Array.from(elements);
  } else {
    return [elements];
  }
}

/**
 * Remove loading states
 * @param loadingRegionSelector CSS Selector for parent element that has the aria-busy attribute. This is an accessibility feature that tells assistive technology that an element or region is loading.
 * @param loadingLabelElementSelector CSS Selector for screen-reader-only labels indicating that the region is loading
 */
export function setLoadingToFalse(
  loadingRegionSelector: string,
  loadingLabelElementSelector?: string
) {
  const loadingRegion = document.querySelector(
    `${loadingRegionSelector}[aria-busy]`
  );
  const loadingLabel = document.querySelector(loadingLabelElementSelector);

  loadingRegion?.setAttribute("aria-busy", "false");
  loadingLabel?.remove();
}
